// src/pages/blog/BlogHome.jsx
// Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar,
  FiClock,
  FiEye,
  FiSearch,
  FiX,
  FiTag,
  FiBookOpen,
  FiChevronLeft,
  FiChevronRight,
  FiTrendingUp,
  FiGrid,
  FiFilter,
  FiChevronDown,
  FiArrowRight,
  FiRss,
  FiStar,
  FiZap,
  FiList,
  FiGrid as FiGridView,
} from 'react-icons/fi';
import { useBlog } from '../../hooks/useBlog';
import blogSearchIndex from '../../services/blogSearchService';
// ✅ REMOVED: ogImageService - No client-side OG generation
// import { ogImageService } from '../../services/ogImageService';

// =============================================
// ANIMATION VARIANTS
// =============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// =============================================
// HERO SECTION - ORIGINAL STYLE RESTORED
// =============================================

const BlogHero = () => {
  return (
    <div className="relative mb-16 overflow-hidden rounded-3xl">
      <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-transparent to-black/5 dark:from-white/5 dark:to-white/5" />
      <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000" />

      <div className="relative px-6 py-16 md:py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6 text-sm"
          style={{ backgroundColor: 'var(--border-light)' }}
        >
          <FiBookOpen className="w-4 h-4" />
          <span>Latest Insights</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 tracking-tight"
          style={{ color: 'var(--text)' }}
        >
          Stories & Insights
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-lg md:text-xl max-w-2xl mx-auto mb-8"
          style={{ color: 'var(--muted)' }}
        >
          Exploring design, development, and creativity through thoughtful
          articles and practical insights.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex items-center justify-center gap-4 flex-wrap"
        >
          <Link
            to="/blog/rss.xml"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all hover:gap-3"
            style={{
              backgroundColor: 'var(--border-light)',
              color: 'var(--text)',
            }}
          >
            <FiRss className="w-4 h-4 text-orange-500" />
            Subscribe to RSS
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

// =============================================
// BLOG CARD - FIXED FOR BOTH GRID AND LIST VIEW
// =============================================

const BlogCard = ({ blog, featured = false, viewMode = 'grid' }) => {
  if (!blog) return null;

  const isListView = viewMode === 'list';

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to={`/blog/${blog.slug}`}
        className={`group block rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
          featured && !isListView ? 'md:flex md:col-span-2' : ''
        } ${isListView ? 'flex flex-col md:flex-row' : ''}`}
        style={{ borderColor: 'var(--border)' }}
      >
        {/* Image */}
        {blog.featuredImage && (
          <div
            className={`relative overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 ${
              isListView ? 'md:w-64 h-48 md:h-48' : ''
            } ${featured && !isListView ? 'md:w-1/2 h-64 md:h-auto' : 'h-48'}`}
          >
            <img
              src={blog.featuredImage}
              alt={blog.imageAlt || blog.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {featured && !isListView && (
              <div className="absolute top-4 left-4 px-2 py-1 rounded-md text-xs font-medium bg-black/70 text-white backdrop-blur-sm flex items-center gap-1.5">
                <FiStar className="w-3 h-3 text-yellow-400" />
                Featured
              </div>
            )}
            {/* Reading Time Badge */}
            <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md text-[10px] font-medium bg-black/60 text-white backdrop-blur-sm flex items-center gap-1">
              <FiClock className="w-3 h-3" />
              <span>{blog.readingTime || 5} min</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className={`p-5 flex-1 ${featured && !isListView ? 'md:w-1/2' : ''}`}
        >
          {/* Meta Info */}
          <div
            className="flex flex-wrap items-center gap-2 text-xs mb-3"
            style={{ color: 'var(--muted)' }}
          >
            {blog.category && (
              <span
                className="px-2 py-1 rounded-full"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                {blog.category}
              </span>
            )}
            <div className="flex items-center gap-1">
              <FiCalendar className="w-3 h-3" />
              <span>
                {blog.publishedAt
                  ? new Date(blog.publishedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'Draft'}
              </span>
            </div>
            {!isListView && (
              <div className="flex items-center gap-1">
                <FiClock className="w-3 h-3" />
                <span>{blog.readingTime || 5} min read</span>
              </div>
            )}
          </div>

          {/* Title */}
          <h3
            className={`font-semibold mb-2 group-hover:underline line-clamp-2 ${
              featured && !isListView ? 'text-2xl md:text-3xl' : 'text-xl'
            }`}
            style={{ color: 'var(--text)' }}
          >
            {blog.title}
          </h3>

          {/* Excerpt - Hide in list view */}
          {!isListView && (
            <p
              className="text-sm line-clamp-2 mb-3"
              style={{ color: 'var(--muted)' }}
            >
              {blog.excerpt}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-3 text-xs"
              style={{ color: 'var(--muted)' }}
            >
              <div className="flex items-center gap-1">
                <FiEye className="w-3 h-3" />
                <span>{blog.views || 0}</span>
              </div>
              {blog.tags && blog.tags.length > 0 && (
                <div className="flex items-center gap-1">
                  <FiTag className="w-3 h-3" />
                  <span className="truncate max-w-[60px]">{blog.tags[0]}</span>
                </div>
              )}
            </div>
            <span
              className="text-sm font-medium group-hover:underline flex items-center gap-1"
              style={{ color: 'var(--text)' }}
            >
              Read More{' '}
              <FiArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

// =============================================
// BLOG GRID - FIXED WITH FEATURED BLOG
// =============================================

const BlogGrid = ({ blogs, viewMode = 'grid' }) => {
  if (!blogs || blogs.length === 0) return null;

  // Find the featured blog (where featured === true)
  const featuredBlog = blogs.find(blog => blog.featured === true);

  // Get remaining blogs (excluding the featured one)
  const remainingBlogs = featuredBlog
    ? blogs.filter(blog => blog.id !== featuredBlog.id)
    : blogs;

  // If no featured blog, show all blogs in regular grid
  if (!featuredBlog) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className={`grid grid-cols-1 ${
          viewMode === 'grid'
            ? 'md:grid-cols-2 lg:grid-cols-3'
            : 'md:grid-cols-1'
        } gap-6`}
      >
        {blogs.map(blog => (
          <BlogCard key={blog.id} blog={blog} viewMode={viewMode} />
        ))}
      </motion.div>
    );
  }

  // If list view, show all blogs as list
  if (viewMode === 'list') {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {blogs.map(blog => (
          <BlogCard key={blog.id} blog={blog} viewMode={viewMode} />
        ))}
      </motion.div>
    );
  }

  // Grid view with featured blog
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Featured Blog - Full width */}
      <div className="grid grid-cols-1">
        <BlogCard blog={featuredBlog} featured={true} viewMode={viewMode} />
      </div>

      {/* Regular Blogs Grid */}
      {remainingBlogs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {remainingBlogs.map(blog => (
            <BlogCard key={blog.id} blog={blog} viewMode={viewMode} />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// =============================================
// INSTANT SEARCH COMPONENT
// =============================================

const InstantSearch = ({
  onSearch,
  placeholder = 'Search articles...',
  initialValue = '',
}) => {
  const [query, setQuery] = useState(initialValue);
  const inputRef = useRef(null);

  const handleInputChange = e => {
    const newValue = e.target.value;
    setQuery(newValue);
    onSearch(newValue);
  };

  const handleClear = () => {
    setQuery('');
    onSearch('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <FiSearch
        className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
        style={{ color: 'var(--muted)' }}
      />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="w-full pl-12 pr-12 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-transparent"
        style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
      />
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          <FiX className="w-4 h-4" style={{ color: 'var(--muted)' }} />
        </button>
      )}
    </div>
  );
};

// =============================================
// BLOG CATEGORIES COMPONENT
// =============================================

const BlogCategories = ({ categories, selectedCategory, onCategorySelect }) => {
  if (!categories || categories.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-sm font-medium flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiGrid className="w-4 h-4" />
          Browse by Category
        </h3>
        <span className="text-xs" style={{ color: 'var(--muted)' }}>
          {categories.length} categories
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onCategorySelect('')}
          className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
            !selectedCategory
              ? 'bg-black text-white dark:bg-white dark:text-black shadow-md'
              : 'border hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ borderColor: 'var(--border)' }}
        >
          All Posts
        </button>
        {categories.map(category => (
          <button
            key={category.name}
            onClick={() => onCategorySelect(category.name)}
            className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
              selectedCategory === category.name
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-md'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            {category.name}
            <span className="ml-1 text-xs opacity-60">({category.count})</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// =============================================
// BLOG TAGS COMPONENT
// =============================================

const BlogTags = ({ tags, selectedTag, onTagSelect }) => {
  const [showAll, setShowAll] = useState(false);
  if (!tags || tags.length === 0) return null;

  const displayTags = showAll ? tags : tags.slice(0, 12);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-sm font-medium flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiTag className="w-4 h-4" />
          Popular Tags
        </h3>
        {tags.length > 12 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-xs hover:underline"
            style={{ color: 'var(--muted)' }}
          >
            {showAll ? 'Show less' : `Show all (${tags.length})`}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {displayTags.map(tag => (
          <button
            key={tag.name}
            onClick={() =>
              onTagSelect(selectedTag === tag.name ? '' : tag.name)
            }
            className={`px-2.5 py-1 rounded-full text-xs transition-all duration-200 ${
              selectedTag === tag.name
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-md'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            #{tag.name}
            <span className="ml-1 opacity-60">({tag.count})</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// =============================================
// SORTING FILTERS COMPONENT
// =============================================

const SortingFilters = ({ sortBy, sortOrder, onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const sortOptions = [
    { value: 'publishedAt', label: 'Most Recent', icon: FiCalendar },
    { value: 'views', label: 'Most Popular', icon: FiTrendingUp },
    { value: 'readingTime', label: 'Short Reads', icon: FiClock },
    { value: 'title', label: 'Alphabetical', icon: FiGrid },
  ];

  const currentOption =
    sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
        style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
      >
        <FiFilter className="w-4 h-4" />
        <span>Sort: {currentOption.label}</span>
        <FiChevronDown
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute top-full right-0 mt-2 w-48 rounded-xl shadow-lg overflow-hidden border z-50 animate-fade-in-up"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            {sortOptions.map(option => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onSortChange(option.value, sortOrder);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5 ${
                    sortBy === option.value
                      ? 'bg-black/10 dark:bg-white/10'
                      : ''
                  }`}
                  style={{ color: 'var(--text)' }}
                >
                  <Icon className="w-4 h-4" />
                  <span>{option.label}</span>
                  {sortBy === option.value && (
                    <span className="ml-auto text-xs">✓</span>
                  )}
                </button>
              );
            })}
            <div
              className="h-px my-1"
              style={{ backgroundColor: 'var(--border)' }}
            />
            <button
              onClick={() => {
                onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
            >
              {sortOrder === 'desc' ? '↓' : '↑'} Order:{' '}
              {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// =============================================
// VIEW TOGGLE COMPONENT
// =============================================

const ViewToggle = ({ view, onViewChange }) => {
  return (
    <div
      className="flex items-center p-0.5 rounded-lg border"
      style={{ borderColor: 'var(--border)' }}
    >
      <button
        onClick={() => onViewChange('grid')}
        className={`p-1.5 rounded transition-all duration-200 ${
          view === 'grid'
            ? 'bg-black text-white dark:bg-white dark:text-black'
            : 'hover:bg-black/5 dark:hover:bg-white/5'
        }`}
        style={{ color: view === 'grid' ? undefined : 'var(--text)' }}
        title="Grid View"
      >
        <FiGridView className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange('list')}
        className={`p-1.5 rounded transition-all duration-200 ${
          view === 'list'
            ? 'bg-black text-white dark:bg-white dark:text-black'
            : 'hover:bg-black/5 dark:hover:bg-white/5'
        }`}
        style={{ color: view === 'list' ? undefined : 'var(--text)' }}
        title="List View"
      >
        <FiList className="w-4 h-4" />
      </button>
    </div>
  );
};

// =============================================
// BLOG PAGINATION COMPONENT
// =============================================

const BlogPagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div
      className="flex items-center justify-center gap-2 mt-12 pt-8 border-t"
      style={{ borderColor: 'var(--border)' }}
    >
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--border)' }}
      >
        <FiChevronLeft className="w-4 h-4" />
      </button>

      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          className={`min-w-[40px] h-10 rounded-lg transition-all duration-200 ${
            currentPage === page
              ? 'bg-black text-white dark:bg-white dark:text-black shadow-md'
              : page === '...'
                ? 'cursor-default'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ borderColor: 'var(--border)' }}
          disabled={page === '...'}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--border)' }}
      >
        <FiChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// =============================================
// SKELETON LOADER
// =============================================

const SkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div
          key={i}
          className="rounded-xl overflow-hidden border animate-pulse"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="h-48 bg-gray-200 dark:bg-gray-800"></div>
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <div className="h-6 w-16 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
              <div className="h-6 w-20 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
            </div>
            <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-5/6"></div>
            <div className="flex justify-between">
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-20"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// =============================================
// MAIN BLOG HOME COMPONENT
// =============================================

const BlogHome = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('publishedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredData, setFilteredData] = useState({
    blogs: [],
    totalItems: 0,
    totalPages: 1,
  });
  const [isSearchReady, setIsSearchReady] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // ✅ REMOVED: metaData - No client-side OG generation
  // Social media scrapers get OG tags from server API
  // The OG tags are now served via /api/social-preview?page=blog

  // Initialize search index
  useEffect(() => {
    const initSearch = async () => {
      try {
        await blogSearchIndex.loadIndex();
        setIsSearchReady(true);

        const initialResult = blogSearchIndex.search('', {
          sortBy,
          sortOrder,
          limit: 9,
          page: 1,
        });
        setFilteredData(initialResult);
        setCategories(blogSearchIndex.getCategories());
        setTags(blogSearchIndex.getTags());
      } catch (err) {
        console.error('Failed to initialize search:', err);
        setError(err.message);
      } finally {
        setIsInitialLoad(false);
      }
    };

    initSearch();
  }, []);

  // Update search results when filters change
  useEffect(() => {
    if (!isSearchReady) return;

    const result = blogSearchIndex.search(searchTerm, {
      category: selectedCategory,
      tag: selectedTag,
      sortBy,
      sortOrder,
      limit: 9,
      page: currentPage,
    });

    setFilteredData(result);
  }, [
    searchTerm,
    selectedCategory,
    selectedTag,
    sortBy,
    sortOrder,
    currentPage,
    isSearchReady,
  ]);

  const handleSearch = useCallback(term => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const handleCategoryChange = useCallback(category => {
    setSelectedCategory(category);
    setSelectedTag('');
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleTagChange = useCallback(tag => {
    setSelectedTag(tag);
    setSelectedCategory('');
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedTag('');
    setSortBy('publishedAt');
    setSortOrder('desc');
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = searchTerm || selectedCategory || selectedTag;
  const displayBlogs = filteredData.blogs || [];
  const totalItems = filteredData.totalItems || 0;
  const totalPages = filteredData.totalPages || 1;

  // Generate schema for blog listing
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'WD Hajbul Blog',
    description:
      'Insights, stories, and expertise on design, development, and creativity.',
    url: 'https://wdhejbul.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'WD Hajbul Creative Studio',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wdhejbul.com/logo.png',
      },
    },
    blogPost: displayBlogs.slice(0, 10).map(blog => ({
      '@type': 'BlogPosting',
      headline: blog.title,
      description: blog.excerpt,
      url: `https://wdhejbul.com/blog/${blog.slug}`,
      datePublished: blog.publishedAt,
      author: {
        '@type': 'Person',
        name: blog.author?.name || 'WD Hajbul',
      },
      image: blog.featuredImage,
    })),
  };

  if (isInitialLoad) {
    return (
      <>
        <Helmet>
          <title>Blog | WD Hajbul - Insights & Stories</title>
          <meta
            name="description"
            content="Explore insights, stories, and expertise from WD Hajbul on design, development, and creativity."
          />
          <meta
            name="keywords"
            content="blog, design, development, creativity, insights, articles, tutorials, web design, UX design, UI design, graphic design, branding, product design"
          />
          <link rel="canonical" href="https://wdhejbul.com/blog" />
          <meta name="robots" content="index, follow" />
          <link
            rel="alternate"
            type="application/rss+xml"
            title="WD Hajbul Blog RSS"
            href="/blog/rss.xml"
          />
          <script type="application/ld+json">
            {JSON.stringify(blogSchema)}
          </script>
        </Helmet>
        <div className="min-h-screen pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <BlogHero />
            <div className="mb-8">
              <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse mb-6"></div>
              <div className="h-20 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse mb-6"></div>
              <div className="h-20 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
            </div>
            <SkeletonLoader />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Blog | WD Hajbul - Insights & Stories</title>
          <meta
            name="description"
            content="Explore insights, stories, and expertise from WD Hajbul."
          />
          <link rel="canonical" href="https://wdhejbul.com/blog" />
          <meta name="robots" content="index, follow" />
        </Helmet>
        <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <FiBookOpen className="w-10 h-10 text-red-500" />
            </div>
            <h2
              className="text-xl font-semibold mb-2"
              style={{ color: 'var(--text)' }}
            >
              Unable to Load Posts
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/social-preview?page=blog
      */}
      <Helmet>
        <title>Blog | WD Hajbul - Insights & Stories</title>
        <meta
          name="description"
          content="Explore insights, stories, and expertise from WD Hajbul on design, development, and creativity."
        />
        <meta
          name="keywords"
          content="blog, design, development, creativity, insights, articles, tutorials, web design, UX design, UI design, graphic design, branding, product design"
        />
        <link rel="canonical" href="https://wdhejbul.com/blog" />
        <meta name="robots" content="index, follow" />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="WD Hajbul Blog RSS"
          href="/blog/rss.xml"
        />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 max-sm:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <BlogHero />

          {/* Instant Search Section */}
          <div className="mb-8">
            <InstantSearch
              onSearch={handleSearch}
              placeholder="Search articles by title, category, or tags..."
              initialValue={searchTerm}
            />
          </div>

          {/* Categories Section */}
          <BlogCategories
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategoryChange}
          />

          {/* Tags Section */}
          <BlogTags
            tags={tags}
            selectedTag={selectedTag}
            onTagSelect={handleTagChange}
          />

          {/* Filters Bar */}
          <div
            className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center gap-3 flex-wrap">
              <SortingFilters
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
              />

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm hover:underline flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiX className="w-3 h-3" />
                  Clear all filters
                </button>
              )}
            </div>

            <div className="flex items-center gap-4">
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                {totalItems} {totalItems === 1 ? 'post' : 'posts'} found
                {searchTerm && ` for "${searchTerm}"`}
              </div>
              <ViewToggle view={viewMode} onViewChange={setViewMode} />
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div
              className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--border-light)' }}
            >
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                Active filters:
              </span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-black/10 dark:bg-white/10">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => handleSearch('')}
                    className="hover:opacity-70 ml-1"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-black/10 dark:bg-white/10">
                  Category: {selectedCategory}
                  <button
                    onClick={() => handleCategoryChange('')}
                    className="hover:opacity-70 ml-1"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedTag && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-black/10 dark:bg-white/10">
                  Tag: #{selectedTag}
                  <button
                    onClick={() => handleTagChange('')}
                    className="hover:opacity-70 ml-1"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Blog Grid */}
          <AnimatePresence mode="wait">
            {displayBlogs.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-20"
              >
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <FiBookOpen
                    className="w-10 h-10"
                    style={{ color: 'var(--muted)' }}
                  />
                </div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  No posts found
                </h3>
                <p
                  className="text-base max-w-md mx-auto"
                  style={{ color: 'var(--muted)' }}
                >
                  {hasActiveFilters
                    ? 'No posts match your current filters. Try adjusting your search criteria.'
                    : 'No blog posts have been published yet. Check back later for new content!'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                    style={{
                      borderColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Clear all filters
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <BlogGrid blogs={displayBlogs} viewMode={viewMode} />

                {totalPages > 1 && (
                  <BlogPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default BlogHome;
